// AI-ASSISTED: generated with claude-code on 2026-08-18, reviewed by Yoann Hertienne
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useKubernetesObjects } from '@backstage/plugin-kubernetes-react';
import {
  ClusterObjects,
  DeploymentFetchResponse,
  KUBERNETES_LABEL_SELECTOR_QUERY_ANNOTATION,
} from '@backstage/plugin-kubernetes-common';
import { TileBoundary } from '../team-landing/TileBoundary';

// The Kubernetes "recommended labels" carry the running app version on the
// Deployment pod template. The ADO release pipeline templates it to the image
// tag (e.g. `1.2.0-alpha.9`); see docs/cf-onboarding.md.
// https://kubernetes.io/docs/concepts/overview/working-with-objects/common-labels/
const VERSION_LABEL = 'app.kubernetes.io/version';

type Deployment = DeploymentFetchResponse['resources'][number];

// A Deployment can advertise a "last updated" moment through its status
// conditions; fall back to its creation timestamp. Rendered in UTC so the
// value is stable regardless of the viewer's locale/timezone.
const lastSeenOf = (dep: Deployment): string => {
  const candidates: Array<Date | string | undefined> = [
    ...(dep.status?.conditions?.map(c => c.lastUpdateTime) ?? []),
    dep.metadata?.creationTimestamp,
  ];
  const times = candidates
    .filter((t): t is Date | string => Boolean(t))
    .map(t => new Date(t).getTime())
    .filter(t => !Number.isNaN(t));

  if (times.length === 0) return 'unknown';
  return `${new Date(Math.max(...times)).toISOString().slice(0, 16).replace('T', ' ')} UTC`;
};

type ClusterVersion = {
  cluster: string;
  version: string | undefined;
  lastSeen: string;
};

// Reduce a cluster's fetch responses to a single deployed-version row: the
// first Deployment that carries the version label wins; if none do we still
// surface the cluster (version undefined → "label not set"). Clusters with no
// Deployment objects at all are dropped by the caller.
const toClusterVersion = (item: ClusterObjects): ClusterVersion | undefined => {
  const deployments = item.resources
    .filter((r): r is DeploymentFetchResponse => r.type === 'deployments')
    .flatMap(r => r.resources);

  if (deployments.length === 0) return undefined;

  const labelled = deployments.find(d => d.metadata?.labels?.[VERSION_LABEL]);
  const chosen = labelled ?? deployments[0];

  return {
    cluster: item.cluster.title ?? item.cluster.name,
    version: labelled?.metadata?.labels?.[VERSION_LABEL],
    lastSeen: lastSeenOf(chosen),
  };
};

const DeployedVersionsCardBody = () => {
  const { entity } = useEntity();
  const { kubernetesObjects, loading, error } = useKubernetesObjects(entity);

  // Hide the card entirely for components that are not wired to Kubernetes —
  // truthful "no data" rather than an empty shell (spec FR-008).
  if (!entity.metadata.annotations?.[KUBERNETES_LABEL_SELECTOR_QUERY_ANNOTATION]) {
    return null;
  }

  const rows = (kubernetesObjects?.items ?? [])
    .map(toClusterVersion)
    .filter((row): row is ClusterVersion => row !== undefined);

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Deployed versions
        </Typography>

        {error && (
          <Typography variant="body2" color="error">
            Could not read deployed versions: {error}
          </Typography>
        )}

        {!error && loading && rows.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Loading deployed versions…
          </Typography>
        )}

        {!error && !loading && rows.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No deployed versions found in any cluster.
          </Typography>
        )}

        {rows.length > 0 && (
          <Table size="small" aria-label="Deployed versions per cluster">
            <TableHead>
              <TableRow>
                <TableCell>Cluster</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Last Seen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => (
                <TableRow key={row.cluster}>
                  <TableCell component="th" scope="row">
                    {row.cluster}
                  </TableCell>
                  <TableCell>
                    {row.version ? (
                      <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace' }}>
                        {row.version}
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        component="span"
                        color="text.secondary"
                        sx={{ fontStyle: 'italic' }}
                      >
                        label not set
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" component="span" color="text.secondary">
                      {row.lastSeen}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export const DeployedVersionsCard = () => (
  <TileBoundary tool="Deployed versions">
    <DeployedVersionsCardBody />
  </TileBoundary>
);
